import React, { useState, useEffect } from "react";
import "./styles/PacientesHistorico.css"; // Ajuste o caminho conforme sua estrutura
import { FaStar, FaRegStar, FaStarHalfAlt } from "react-icons/fa";
import { useNotification } from "../../components/NotificationContext";

const PacientesHistorico = () => {
  const { showNotification } = useNotification();
  const [consultations, setConsultations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Estados para avaliação
  const [showModal, setShowModal] = useState(false);
  const [selectedConsulta, setSelectedConsulta] = useState(null); // Armazena a consulta que será avaliada
  const [selectedRating, setSelectedRating] = useState(null);
  const [evaluationComment, setEvaluationComment] = useState("");

  // Descrições de cada nota (0..5 ou 1..5, conforme preferir)
  const ratingDescriptions = [
    "Péssima experiência.",
    "Ruim.",
    "Regular.",
    "Boa.",
    "Muito boa.",
    "Excelente!"
  ];

  // Carrega as consultas ao montar o componente
  useEffect(() => {
    fetchConsultations();
  }, []);

  // Busca as consultas do paciente na API
  const fetchConsultations = async () => {
    setLoading(true);
    setError(null);

    const patientId = localStorage.getItem("patientId");
    if (!patientId) {
      setError("ID do paciente não encontrado. Faça login novamente.");
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(
        "https://x8ki-letl-twmt.n7.xano.io/api:bf7mCpGF/busca_consultas",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ paciente_id: patientId }),
        }
      );

      if (!response.ok) {
        throw new Error("Erro ao buscar consultas do paciente.");
      }

      const data = await response.json();
      setConsultations(data); // Armazena as consultas no estado
    } catch (err) {
      console.error("Erro ao buscar consultas:", err);
      setError("Não foi possível carregar as consultas. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  // Abre o modal para avaliar uma consulta
  const openEvaluationModal = (consulta) => {
    setSelectedConsulta(consulta);
    setSelectedRating(null); // Reseta a nota
    setEvaluationComment(""); // Limpa comentário
    setShowModal(true);
  };

  // Fecha o modal
  const closeModal = () => {
    setShowModal(false);
    setSelectedConsulta(null);
    setSelectedRating(null);
    setEvaluationComment("");
  };

  // Seleciona a nota de avaliação
  const selectRating = (rating) => {
    setSelectedRating(rating);
  };

  // Envia a avaliação para a API (PATCH)
  const submitEvaluation = async () => {
    if (!selectedRating && selectedRating !== 0) {
      showNotification("Por favor, selecione uma nota.", "warning");
      return;
    }

    if (!selectedConsulta) {
      showNotification("Consulta não encontrada. Recarregue a página e tente novamente.", "error");
      return;
    }

    try {
      const url = `https://x8ki-letl-twmt.n7.xano.io/api:bf7mCpGF/consultas/${selectedConsulta.id}`;
      const data = {
        rating: selectedRating,
        comment: evaluationComment,
      };

      const response = await fetch(url, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        throw new Error("Erro ao enviar a avaliação.");
      }

      await response.json();
      showNotification("Avaliação enviada com sucesso!", "success");

      // Fecha o modal
      closeModal();
      // Recarrega as consultas para atualizar o status "foi_avaliada"
      fetchConsultations();
    } catch (err) {
      console.error("Erro ao avaliar consulta:", err);
      showNotification("Ocorreu um erro ao enviar sua avaliação. Tente novamente mais tarde.", "error");
    }
  };

  return (
    <div className="pacientes-historico">
      <h2>Histórico de Consultas</h2>

      {loading && <p>Carregando consultas...</p>}
      {error && <p style={{ color: "red" }}>{error}</p>}

      {/* Se não estiver carregando e não houver erro */}
      {!loading && !error && (
        <>
          {consultations.length === 0 ? (
            <p>Nenhuma consulta encontrada.</p>
          ) : (
            <ul className="consultations-list">
              {consultations.map((consulta) => {
                const formattedDate = new Date(
                  consulta.data_consulta
                ).toLocaleDateString();
                const valorPago = parseFloat(consulta.valor_pago || 0);
                const cashbackCalculado = (valorPago * 0.3).toFixed(2);

                return (
                  <li key={consulta.id} className="consultation-item">
                   
                   <div className="consultation-left">
                    <span className="consultation-title"><strong>{consulta.titulo}</strong></span>
                    <span className="consultation-value">R$ {valorPago.toFixed(2)}</span>
                  </div>

                  <div className="consultation-center">
                    <span className="consultation-cashback">Cashback: R${cashbackCalculado}</span>
                  </div>

                  <div className="consultation-right">
                    <span className="consultation-date">Data: {formattedDate}</span>
                    {consulta.foi_avaliada ? (
                      <span className="evaluation-status">Consulta Avaliada</span>
                    ) : (
                      <button
                        className="evaluate-button"
                        onClick={() => openEvaluationModal(consulta)}
                      >
                        Avalie essa consulta
                      </button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </>
      )}

      {/* Modal de avaliação (exibido condicionalmente) */}
      {showModal && (
        <div className="evaluation-modal-overlay">
          <div className="evaluation-modal">
            <span className="evaluation-close" onClick={closeModal}>
              &times;
            </span>
            <h3>Avaliar Consulta</h3>
            <p>
              Consulta:{" "}
              <strong>{selectedConsulta ? selectedConsulta.titulo : ""}</strong>
            </p>

            <div className="rating-section">
              <p>Selecione uma nota:</p>
              <div className="star-rating">
                {[0, 1, 2, 3, 4, 5].map((r) => (
                  <div
                    key={r}
                    className={`star-container ${r === selectedRating ? "selected" : ""}`}
                    onClick={() => selectRating(r)}
                  >
                    <div className="star-icon">
                      {r === 0 ? (
                        <span className="zero-rating">0</span>
                      ) : (
                        <FaStar className={r <= selectedRating ? "filled" : ""} />
                      )}
                    </div>
                    <span className="star-value">{r}</span>
                  </div>
                ))}
              </div>
              <p className="rating-description">
                {selectedRating !== null
                  ? ratingDescriptions[selectedRating]
                  : "Selecione uma nota acima."}
              </p>
            </div>

            <div className="comment-section">
              <label htmlFor="evaluation-comment">Comentário (opcional):</label>
              <textarea
                id="evaluation-comment"
                value={evaluationComment}
                onChange={(e) => setEvaluationComment(e.target.value)}
              />
            </div>

            <button className="submit-evaluation-btn" onClick={submitEvaluation}>
              Enviar Avaliação
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PacientesHistorico;
